import { Component, computed, effect, forwardRef, inject, input, OnInit, output, signal, ViewChild } from '@angular/core';
import { ControlValueAccessor, FormControl, FormsModule, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { OptionLabelPipe } from '../../../pipes/option-label.pipe';
import { UrlMakerPipe } from '../../../pipes/url-maker.pipe';
import { jaroWinklerDistance } from '../../../utils/utils';
import { AddItemConfirmDialogComponent } from '../../dialogs/add-item-confirm-dialog/add-item-confirm-dialog.component';
import { MatTooltip, MatTooltipModule } from '@angular/material/tooltip';

export interface SelectOption {
    id: string | number;
    name: string;
    alternateName?: string;
    icons: string[];
}

@Component({
    selector: 'app-searchable-select',
    standalone: true,
    imports: [
    FormsModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatInputModule,
    MatFormFieldModule,
    MatIconModule,
    MatSnackBarModule,
    MatDialogModule,
    OptionLabelPipe,
    MatButtonModule,
    UrlMakerPipe,
    MatTooltipModule
],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => SearchableSelectComponent),
            multi: true,
        },
    ],
    templateUrl: './searchable-select.component.html',
    styleUrl: './searchable-select.component.scss',

})
export class SearchableSelectComponent implements OnInit, ControlValueAccessor {
    readonly _snackBar = inject(MatSnackBar);
    readonly dialog = inject(MatDialog);
    @ViewChild(MatAutocompleteTrigger) autocomplete!: MatAutocompleteTrigger;

    itemName = input<string>('');
    label = input<string>('');
    prefixIcon = input<string>('');
    hint = input<string>('');
    options = input<SelectOption[]>([]);
    width = input<Number>(350);
    showLink = input<boolean>(false);
    linkItem = input<string>('');
    displayAlternateName = input<boolean>(false);

    // The value is the id of the selected option. 
    // It is set by the parent component via writeValue, 
    // and updated by the component when an option is selected.
    // protected value = signal<number | string | null>(null);

    // selectedOption = computed(() => {
    //     // console.debug('selectedOption', this.value(), this.options().length);
    //     const o = this.options().find(o => o.id === this.value()) ?? null;
    //     // console.debug('\tselectedOption', o);
    //     // this.internalControl.setValue(o, { emitEvent: false });
    //     return o;
    // });

    // Filtering logic
    filteredOptions = computed(() => {
        // console.debug('filteredOptions', this.searchTerm(), this.options().length);
        const term = this.searchTerm().trim().toLowerCase();

        return this.options().filter(o => {
            const name = o.name.toLowerCase();
            const alt = o.alternateName?.toLowerCase() ?? '';
            return name.includes(term) || alt.includes(term);
        });
    });

    isDisabled = signal(false);

    mouseDown: boolean = false;
    enterPressed: boolean = false;
    dialogOpen: boolean = false;

    internalControl = new FormControl();
    searchTerm = signal<string>('');
    allowNewItem = input<boolean>(false);
    addOption = output<string>();

    constructor() {
        effect(() => {
            if (this.isDisabled()) {
                this.internalControl.disable();
            } else {
                this.internalControl.enable();
            }
        })
    }

    ngOnInit(): void {
        // console.debug('SearchableSelectComponent: ngOnInit', this.itemName(), this.label(), this.options().length);
    }

    get requireSelection(): boolean {
        return !this.allowNewItem();
    }

    // CVA Boilerplate
    onChange = (val: any) => { };
    onTouched = () => { };

    setDisabledState(isDisabled: boolean): void {
        this.isDisabled.set(isDisabled);
        // console.debug('isDisabled', isDisabled);
    }

    // Parent passes the full object or null
    writeValue(val: SelectOption): void {
        console.debug('SearchableSelectComponent writeValue', val, this.options().length);
        this.internalControl.setValue(val, { emitEvent: false });
        this.searchTerm.set(val?.name ?? '');
    }

    registerOnChange(fn: any) {
        this.onChange = fn;
    }

    registerOnTouched(fn: any) {
        this.onTouched = fn;
    }

    onInput(e: Event) {
        // console.log('onInput', (e.target as HTMLInputElement).value)
        this.searchTerm.set((e.target as HTMLInputElement).value);
    }

    /**
     * runs when an option is selected, either by click or enter
     * if click, onOptionMouseDown -> onBlur -> handleSelect
     * if enter, handleSelect -> onEnter -> onBlur (when clicking in another field/button)
     * @param val
     */
    handleSelect(val: any) {
        console.log('handleSelect', val);
        // strip icons (if any)
        // const { id, name } = val;
        this.searchTerm.set(val.name);
        this.onChange(val);
    }

    /**
     * runs when an option is selected by click
     * click -> onOptionMouseDown -> onBlur -> handleSelect
     */

    onOptionMouseDown() {
        // console.log('onOptionMouseDown')
        this.mouseDown = true;
    }

    /**
     * if click on option, onOptionMouseDown -> onBlur -> handleSelect
     * if click outside option, onBlur
     * if enter, handleSelect -> onEnter -> onBlur (when clicking in another field/button)
     * @param event
     * @returns
     */

    onBlur(event: FocusEvent) {
        // console.log('onBlur')

        if (this.mouseDown === true) {
            // an option was clicked (onOptionMouseDown)
            // console.debug('blur: an option was clicked');
            this.mouseDown = false;
            return;
        }

        if (this.enterPressed === true) {
            // enter was pressed
            // console.debug('blur: enterPressed');
            this.enterPressed = false;
            return;
        }

        if (this.dialogOpen) {
            // enter was pressed which opens the confirmation dialog to add a new value
            // console.debug('dialog open');
            return;
        }

        const inputValue = (event.target as HTMLInputElement).value?.trim();
        if (!inputValue) {
            // filter was cleared
            // console.debug('blur after clear');
            this.internalControl.setValue(null);
            this.searchTerm.set(''); // only if you still use it for filtering
            this.onChange(null);
            return
        }

        // this.onTouched();
        const currentText = this.searchTerm().trim().toLowerCase();
        const currentVal = this.internalControl.value;

        // console.debug('onBlur currentText', currentText, 'currentVal', currentVal);

        // A value is selected
        if (currentVal && typeof currentVal === 'object') {
            return;
        }

        if (currentText === undefined || currentText === null || currentText.trim() === '') {
            this.onChange(null);
        }


        const exactMatch = this.options().find(
            opt => opt.name.toLowerCase() === currentText
        );

        if (exactMatch) {
            // Auto-select the match if they typed it perfectly
            // console.log('exactMatch')
            this.internalControl.setValue(exactMatch);
            this.searchTerm.set(exactMatch.name);
            this.onChange(exactMatch);
        } else {
            // console.log('No exactMatch')
            // No match found, requireSelection will clear the input.
            this.internalControl.setValue(null);
            this.searchTerm.set('');
            // We sync that 'null' back to the parent form.
            this.onChange(null);
        }
    }

    /**
     * runs when enter is hit on the input field
     * if click on option, onOptionMouseDown -> onBlur -> handleSelect
     * if click outside option, onBlur
     * if enter, handleSelect -> onEnter -> onBlur (when clicking in another field/button)
     * @param event
     * @returns
     */

    onEnter(event: Event) {
        // console.log('onenter', this.searchTerm(), this.internalControl.value);
        event.preventDefault();
        event.stopPropagation();

        const currentText = this.searchTerm().trim().toLowerCase();
        const currentVal = this.internalControl.value;
        // console.debug('onEnter currentText', currentText, 'currentVal', currentVal);

        if (currentVal && typeof currentVal === 'object') {
            // console.debug('handleSelect has run');
            return;
        }

        this.enterPressed = true;
        this.autocomplete.closePanel();

        const term = this.searchTerm().trim();
        if (!term) {
            // console.debug('empty term');
            let msg = `❌ Please select an option${(this.allowNewItem() ? ' or enter a new one.' : '.')}`
            this._snackBar.open(msg, 'Close', { duration: 3000 });
            this.onTouched();
            return
        }

        // First check that item is not present yet in the list; if it is, add it to the selection
        const exactMatch = this.options().find(
            opt => opt.name.toLowerCase() === term.toLocaleLowerCase()
        );

        if (exactMatch) {
            // Auto-select the match if user typed it correctly
            this.internalControl.setValue(exactMatch);
            this.searchTerm.set(exactMatch.name);
            this.onChange(exactMatch);
            return;
        }
        if (!this.allowNewItem()) {
            // console.debug('not allowing new items');
            return;
        }

        let similarItems = this.options().filter(v => jaroWinklerDistance(v.name, term) > 0.8)
            .map(v => v.name)

        this.dialogOpen = true;
        const dialogRef = this.dialog.open(AddItemConfirmDialogComponent, {
            data: {
                itemName: this.itemName(),
                newItem: term,
                similarItems: similarItems,
            }
        });
        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                // Push new item to the parent component
                this.addOption.emit(term);
                this.searchTerm.set(term)
                this.dialogOpen = false;
            } else {
                this.internalControl.setValue(null);
                this.searchTerm.set('');
                this.dialogOpen = false;
                // console.debug(`cancelled ${this.filteredOptions().length} >${term}< >${this.searchTerm()}< >${this.internalControl.value}<`)
            }
        });


    }

    displayFn = (o: SelectOption | null): string => {
        return o?.name ?? '';
    };

    compareFn = (a: any, b: any) => a && b && a.id === b.id;

    openLink(): void {
        // let url = this.form.get('website')?.value
        // if (url) {
        //     if (!/^https?:\/\//i.test(url)) {
        //         url = 'https://' + url;
        //     }
        //     window.open(url, '_blank')
        // }
    }
}